import * as React from 'react';

import { translate } from '~l10n/client';

import { SmallButton } from '~shared/components/buttons';
import { Container } from '~shared/components/Container/Container';
import { Divider } from '~shared/components/Divider';
import TextInput from '~shared/components/TextInput';
import { DeleteWorkspaceDialog } from '~teacher/components/workspaces/DeleteWorkspaceDialog/DeleteWorkspaceDialog';
import {
  useGetWorkspaceDetail,
  useWorkspacePermissionsV2,
} from '~teacher/hook/workspaces';
import { useRenameWorkspace } from '~teacher/hook/workspaces/useRenameWorkspace';

interface SettingsProps {
  workspaceId: string;
}
export function WorkspaceSettings({ workspaceId }: SettingsProps) {
  const {
    permissions: {
      deleteWorkspaceFolder: canDeleteWorkspaceForever,
      renameWorkspace: canRenameWorkspace,
    },
  } = useWorkspacePermissionsV2({ workspaceId });

  const workspaceInfo = useGetWorkspaceDetail({ workspaceId: workspaceId });
  const [showDeleteWorkspaceDialog, setShowDeleteWorkspaceDialog] =
    React.useState(false);

  const [newName, setNewName] = React.useState('');

  // set the field to the workspace name when it loads in
  React.useEffect(
    () => setNewName(workspaceInfo.data?.name ?? ''),
    [workspaceInfo.data?.name]
  );

  const { rename } = useRenameWorkspace(() => setNewName(''));

  const handleNewNameSave = () => {
    if (!workspaceId) return;
    return rename.mutate({
      id: workspaceId,
      name: newName,
    });
  };

  return (
    <div className={'relative mt-4'}>
      <Divider />
      <Container row className={'items-end py-5 text-base'}>
        <Container width={1}>
          <label htmlFor={'name'} className="mb-4 text-blue-900">
            {translate('Shared Library name')}
          </label>
          <TextInput
            readOnly={!canRenameWorkspace}
            disabled={!canRenameWorkspace}
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            id={'name'}
            placeholder={translate('Shared Library name')}
          />
        </Container>
        <SmallButton
          className={'ml-3'}
          isDisabled={
            !canRenameWorkspace ||
            newName === '' ||
            newName === workspaceInfo.data?.name
          }
          onClick={handleNewNameSave}
        >
          {translate('Update')}
        </SmallButton>
      </Container>
      <Divider />
      <Container className={'py-5 items-start'}>
        <p className={'text-base mb-2 text-blue-900'}>
          {translate('Delete library')}
        </p>
        <p className={'text-sm text-blue-600 mb-3'}>
          {translate(
            'Deleting the library will irreversibly delete all its quizzes and disband members.'
          )}
        </p>
        <SmallButton
          isDisabled={!canDeleteWorkspaceForever}
          onClick={() => setShowDeleteWorkspaceDialog(true)}
        >
          {translate('Delete Library Forever')}
        </SmallButton>
      </Container>
      <DeleteWorkspaceDialog
        workspaceId={workspaceId}
        isOpen={showDeleteWorkspaceDialog}
        setIsOpen={setShowDeleteWorkspaceDialog}
      />
    </div>
  );
}
